import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { PortalFooter } from "../../../../../../components/molecules/Footer";
import { useNotification } from "../../../../../../hooks/useNotification";
import {
  useGetFilesQuery,
  useGetDocumentCategoriesQuery,
  useLazyGetDocumentSubCategoriesQuery,
  useUpdateFileInModuleMutation,
} from "../../../../../../store/apiSlice/fileApiSlice";
import { useCreateTransferOrderFilesMutation } from "../../../../../../store/apiSlice/orderApiSlice";
import { useCreatePurchaseOrderFilesMutation } from "../../../../../../store/apiSlice/purchaseOrderApiSlice";
import { useCreateSaleOrderFilesMutation } from "../../../../../../store/apiSlice/salesApiSlice";
import { useCreateConsignmentFilesMutation } from "../../../../../../store/apiSlice/shipmentApiSlice";
import { InputText } from "../../../../components/InputText/InputText";
import { SelectDropDown } from "../../Input/SelectDropDown";
import { UploadFile } from "../../Input/UploadFile";
import { FilesDrawer } from "./FilesDrawer";

/**
 * UploadFiles Component
 *
 * Handles both uploading and updating files related to a shipment or consignment.
 * Allows selecting file categories, subcategories, adding reference numbers,
 * and uploading one or more files using form data or an update request.
 *
 * @component
 * @param {Function} closeUploadFiles - Closes the upload drawer.
 * @param {string|number} id - ID of the consignment/shipment to attach files.
 * @param {string} [recordName] - Record name shown in the drawer title when present.
 * @param {Object|null} editUploadFileDetail - Existing file details for edit mode.
 * @param {Function} resetSelectedFiles - Resets selected files in parent component.
 * @param {Function} getFiles - Refetches file list after upload/update.
 */

const UploadFiles = ({
  closeUploadFiles,
  id,
  recordName,
  editUploadFileDetail,
  resetSelectedFiles = () => {},
  getFiles = () => {},
  componentId,
}) => {
  const isEditMode = useMemo(() => (editUploadFileDetail ? true : false), [editUploadFileDetail]);
  const [uploadDetails, setUploadDetails] = useState(
    editUploadFileDetail || {
      category: null,
      subCategory: null,
      reference: null,
    },
  );

  const [files, setFiles] = useState([]);
  const [formError, setFormError] = useState(null);
  const notify = useNotification();

  const attachedFilesParams = new URLSearchParams({
    limit: 100,
    offset: 0,
    mode: "consignment",
    consignment_id_in_module: id,
  }).toString();
  const { data: attachedFilesData, refetch: refetchAttachedFiles } = useGetFilesQuery(
    attachedFilesParams,
    { skip: !id || componentId !== "consignmentDetailFile" },
  );

  const [createConsignmentFiles, { isLoading: createConsignmentLoading }] =
    useCreateConsignmentFilesMutation();
  const [createTransferFiles, { isLoading: createTransferLoading }] =
    useCreateTransferOrderFilesMutation();
  const [createPurchaseFiles, { isLoading: createPurchaseLoading }] =
    useCreatePurchaseOrderFilesMutation();
  const [createSaleOrderFiles, { isLoading: createSaleOrderLoading }] =
    useCreateSaleOrderFilesMutation();
  const [updateConsignmentFile, { isLoading: updateFileLoading }] = useUpdateFileInModuleMutation();

  const isSubmitting =
    createConsignmentLoading ||
    createTransferLoading ||
    createPurchaseLoading ||
    createSaleOrderLoading ||
    updateFileLoading;

  useGetDocumentCategoriesQuery();
  const [getSubCategory] = useLazyGetDocumentSubCategoriesQuery();

  const createFileData = useMemo(
    () => ({
      transferDetailFile: {
        createIdName: "transfer_order",
        createFile: createTransferFiles,
        headerName: "Transfer Order",
      },
      purchaseDetailFile: {
        createIdName: "purchase_order",
        createFile: createPurchaseFiles,
        headerName: "Purchase Order",
      },
      consignmentDetailFile: {
        createIdName: "consignment",
        createFile: createConsignmentFiles,
        headerName: "Shipment",
      },
      salesOrderDetailFile: {
        createIdName: "sale_order",
        createFile: createSaleOrderFiles,
        headerName: "Sales Order",
      },
    }),
    [createTransferFiles, createConsignmentFiles, createPurchaseFiles, createSaleOrderFiles],
  );

  const { documentCategoriesSelection, documentSubCategoriesSelection } = useSelector(
    state => state.file,
  );

  useEffect(() => {
    if (uploadDetails.category) {
      getSubCategory(
        new URLSearchParams({ document_category_id: uploadDetails.category }).toString(),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadDetails.category]);

  const handleDropDown = (value, name) => {
    const data = { ...uploadDetails, [name]: Number(value) };
    if (name === "category") {
      data["subCategory"] = null;
    }
    setUploadDetails(data);
  };
  const handleInputText = e => {
    const { value, name } = e.target;
    setUploadDetails({ ...uploadDetails, [name]: value });
  };

  const findCat = (id, items = []) => {
    const nameOfVal = items.find(item => item.id === id)?.name;
    return nameOfVal;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (files.length === 0 && !isEditMode) {
      setFormError("At least one file is required.");
      setTimeout(() => {
        setFormError(null);
      }, 3000);
      return;
    }

    try {
      // Prepare form data
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append("files", file));
      formData.append("document_category", uploadDetails.category || "");
      formData.append("document_sub_category", uploadDetails.subCategory || "");
      formData.append("reference_no", uploadDetails.reference || "");
      formData.append(createFileData[componentId]["createIdName"], id);

      if (isEditMode) {
        const fileData = {
          document_category: uploadDetails.category,
          document_sub_category: uploadDetails.subCategory,
          reference_no: uploadDetails.reference,
          id: uploadDetails.id,
          file_name: uploadDetails.file_name,
        };
        const response = await updateConsignmentFile(fileData).unwrap();
        notify({
          type: "success",
          message: response.detail || "Success! Your files are now updated.",
        }); // Show success notification
      } else {
        const createFile = createFileData[componentId]["createFile"];
        const response = await createFile(formData).unwrap();
        notify({
          type: "success",
          message: response.detail || "Success! Your files are now uploaded.",
        }); // Show success notification
      }
      getFiles();
      if (componentId === "consignmentDetailFile") refetchAttachedFiles();
      closeUploadFiles();
      resetSelectedFiles();
    } catch (error) {
      const err = error?.data;

      const errorMessage = Array.isArray(err?.files)
        ? err?.files.join(", ")
        : err?.detail || error?.message;
      notify({
        type: "error",
        message: errorMessage || "We couldn’t upload the files. Please check and try again.",
      });
    }
  };

  const headerName = createFileData[componentId]["headerName"];
  const drawerTitle = recordName
    ? `${isEditMode ? "Update File" : "Upload Files"}: ${recordName}`
    : `${isEditMode ? "Update File" : "Upload Files"} for ${headerName}`;

  return (
    <FilesDrawer isOpen title={drawerTitle} onClose={closeUploadFiles}>
      <UploadFilesCss className="fileManagerSelect">
        <SelectDropDown
          widthOfSelectElement="100%"
          heightOfSelectElement="36px"
          placeholderForInput="Select a category"
          labelForInput="Category"
          options={documentCategoriesSelection}
          handleSelect={handleDropDown}
          nameOfInput={"category"}
          selectedVal={findCat(uploadDetails.category, documentCategoriesSelection)}
          isOptionWithId={true}
        />

        <SelectDropDown
          widthOfSelectElement="100%"
          heightOfSelectElement="36px"
          placeholderForInput="Select a subcategory"
          labelForInput="Sub Category"
          options={documentSubCategoriesSelection}
          handleSelect={handleDropDown}
          nameOfInput={"subCategory"}
          selectedVal={findCat(uploadDetails.subCategory, documentSubCategoriesSelection)}
          isOptionWithId={true}
        />

        {isEditMode && editUploadFileDetail?.selectedFiles?.length === 1 && (
          <InputText
            labelOfInput={"File Name"}
            text={uploadDetails.file_name}
            setText={handleInputText}
            inputName={"file_name"}
            heightOfInput={"32px"}
            widthOfInput={"100%"}
            placeholder={"File Name "}
            requiredOption={true}
          />
        )}

        {(!isEditMode || editUploadFileDetail?.selectedFiles?.length === 1) && (
          <InputText
            labelOfInput={"Reference No"}
            text={uploadDetails.reference}
            setText={handleInputText}
            inputName={"reference"}
            heightOfInput={"32px"}
            widthOfInput={"100%"}
            placeholder={" "}
          />
        )}

        {!isEditMode && (
          <UploadFile
            labelForInput={"Files"}
            isRequiredField={true}
            files={files}
            setFiles={setFiles}
          />
        )}
        {formError && !isEditMode && <div className="error">{formError}</div>}
        {componentId === "consignmentDetailFile" && (
          <AttachedFilesSection aria-label="Previously attached files">
            <h3>Previously attached files</h3>
            {attachedFilesData?.results?.length ? (
              <ul>
                {attachedFilesData.results.map(file => (
                  <li key={file.id}>
                    <span>{file.file_name || file.file_detail?.name || "Untitled file"}</span>
                    {file.document_category?.name && <small>{file.document_category.name}</small>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No files attached yet.</p>
            )}
          </AttachedFilesSection>
        )}
      </UploadFilesCss>

      <PortalFooter
        className="fileDrawerFooter"
        onApply={handleSubmit}
        onCancel={closeUploadFiles}
        applyTitle="Submit"
        isLoading={isSubmitting}
      />
    </FilesDrawer>
  );
};

export { UploadFiles };

const UploadFilesCss = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px;
  .labelForText {
    > input {
      padding-left: 12px;
    }
  }
  .selectDropDown > .selectElement > {
    .labelForInput {
      color: #4a4a68;
      font-size: 12px;
      font-weight: 600;
      line-height: 18px;
    }
  }
  .error {
    color: var(--DANGER, #cf324a);
  }
`;

const AttachedFilesSection = styled.section`
  border-top: 1px solid var(--PEARL, #ecf1f4);
  margin-top: 8px;
  padding-top: 16px;

  h3 {
    color: var(--GREY, #4a4a68);
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 12px;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 8px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 13px;
  }

  li span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small,
  p {
    color: var(--GREY, #4a4a68);
    margin: 0;
  }
`;
